import React, { useState, useEffect, useCallback, useRef, Suspense, lazy } from 'react';
import Swal from 'sweetalert2';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import AquaLayout from '../../components/layout/AquaLayout';
import LoginPage from './LoginPage';
import {
  registroDiarioAPI,
  usuarioAPI,
  logAPI,
  alertaAPI,
  bitacoraAPI,
  profileAPI,
  lavadoFiltroAPI
} from '../../services/api';
import authAPI from '../../services/auth';
import { mapBackendToJSX, mapUserToJSX, getCurrentHour, getCurrentDate, getNow } from '../../utils/mappings';
import { useTheme } from '../../context/ThemeContext';
import {
  getPendingRegistroCount,
  getPendingRegistrosAsJSX,
  syncOfflineQueue,
  isOfflineError,
} from '../../services/offlineQueue';

const CACHED_USER_KEY = 'aquareport.cachedUser';

// Cada página va en su propio chunk: recharts (Graficas/Gerencia), exceljs
// (Exportar/Importar) y demás dependencias pesadas por página solo se
// descargan cuando el usuario realmente navega ahí, en vez de ir todas en
// el bundle inicial (que antes cargaba las 14 páginas de una sola vez).
const DashboardPage = lazy(() => import('./DashboardPage'));
const RegistrosPage = lazy(() => import('./RegistrosPage'));
const UsuariosPage = lazy(() => import('./UsuariosPage'));
const GraficasPage = lazy(() => import('./GraficasPage'));
const AlertasPage = lazy(() => import('./AlertasPage'));
const LogsPage = lazy(() => import('./LogsPage'));
const BitacoraPage = lazy(() => import('./BitacoraPage'));
const ExportarPage = lazy(() => import('./ExportarPage'));
const EnsayosPage = lazy(() => import('./EnsayosPage'));
const GerenciaPage = lazy(() => import('./GerenciaPage'));
const LavadoFiltrosPage = lazy(() => import('./LavadoFiltrosPage'));
const InventarioPage = lazy(() => import('./InventarioPage'));
const InformesPage = lazy(() => import('./InformesPage'));
const SeguridadPage = lazy(() => import('./SeguridadPage'));
const ImportarPage = lazy(() => import('./ImportarPage'));
const PrediccionPage = lazy(() => import('./PrediccionPage'));
const RegistrosMesPage = lazy(() => import('./RegistrosMesPage'));

export default function AquaApp() {
  const { theme } = useTheme();
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [registros, setRegistros] = useState([]);
  const [logs, setLogs] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [bitacora, setBitacora] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [pendingSyncCount, setPendingSyncCount] = useState(() => getPendingRegistroCount());
  const alertIntervalRef = useRef(null);
  const checkMissingRef = useRef(null);
  // Refs para que los setInterval de abajo lean el valor más reciente de
  // registros/alertas sin tener que recrearse (y perder el conteo de 60s)
  // cada vez que esos arrays cambian — lo que pasaba antes porque estaban
  // en el arreglo de dependencias del useEffect que crea los intervalos.
  const registrosRef = useRef(registros);
  const alertasRef = useRef(alertas);
  useEffect(() => { registrosRef.current = registros; }, [registros]);
  useEffect(() => { alertasRef.current = alertas; }, [alertas]);
  const navigate = useNavigate();
  const location = useLocation();
  const usersLoadedRef = useRef(false);
  const logsLoadedRef = useRef(false);
  const bitacoraLoadedRef = useRef(false);

  const notify = useCallback((msg, type="info") => {
    let title = "Información";
    let icon = "info";
    if (type === "success") { title = "¡Éxito!"; icon = "success"; }
    else if (type === "error") { title = "Error"; icon = "error"; }
    else if (type === "warning") { title = "Advertencia"; icon = "warning"; }
    const isToast = type !== "error";
    
    Swal.fire({
      toast: isToast,
      position: isToast ? 'top-end' : 'center',
      icon,
      title,
      text: msg,
      confirmButtonColor: "var(--primary)",
      timer: isToast ? 3000 : undefined,
      timerProgressBar: isToast,
      showConfirmButton: !isToast
    });
  }, []);

  const addLog = useCallback(async (usuario, accion, detalle) => {
    try {
      await logAPI.create({ usuario, accion, detalle, ip: "127.0.0.1" });
      setLogs(prev => [{
        id: Date.now(), usuario, accion, detalle,
        fecha: getNow(), ip: "127.0.0.1"
      }, ...prev]);
    } catch (e) {
      console.error("Error creating log", e);
    }
  }, []);

  const refreshPendingSync = useCallback(() => {
    const pendingRegistros = getPendingRegistrosAsJSX();
    setPendingSyncCount(pendingRegistros.length);
    return pendingRegistros;
  }, []);

  // Cada recurso se trae por separado y solo cuando hace falta: registros y
  // alertas siempre (los usan el layout y los intervalos de recordatorio sin
  // importar la ruta activa), usuarios/logs/bitacora quedan en carga perezosa
  // disparada por la ruta (ver efecto de abajo), para no pegarle a 5 endpoints
  // cuando solo se está usando /registros.
  const fetchRegistros = useCallback(async () => {
    const pendingRegistros = getPendingRegistrosAsJSX();
    setPendingSyncCount(pendingRegistros.length);
    try {
      const res = await registroDiarioAPI.getAll();
      const regsData = res.data;
      setRegistros([...(regsData.results || regsData).map(mapBackendToJSX), ...pendingRegistros]);
    } catch (e) {
      if (pendingRegistros.length) {
        setRegistros(pendingRegistros);
        notify("Sin conexion: mostrando registros pendientes locales", "warning");
      } else {
        notify("Error al cargar datos", "error");
      }
    }
  }, [notify]);

  const fetchUsuarios = useCallback(async () => {
    try {
      const res = await usuarioAPI.getAll();
      const usrsData = res.data;
      setUsers((usrsData.results || usrsData).map(mapUserToJSX));
    } catch (e) {
      console.error("Error fetching usuarios", e);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const res = await logAPI.getAll();
      const lgsData = res.data;
      setLogs((lgsData.results || lgsData).map(l => ({...l, fecha: l.fecha.replace('T', ' ').split('.')[0]})));
    } catch (e) {
      console.error("Error fetching logs", e);
    }
  }, []);

  const fetchAlertas = useCallback(async () => {
    try {
      const res = await alertaAPI.getAll();
      const alrtsData = res.data;
      setAlertas((alrtsData.results || alrtsData).map(a => ({...a, fecha: a.fecha.replace('T', ' ').split('.')[0]})));
    } catch (e) {
      console.error("Error fetching alertas", e);
    }
  }, []);

  const fetchBitacora = useCallback(async () => {
    try {
      const res = await bitacoraAPI.getAll();
      const bitData = res.data;
      setBitacora((bitData.results || bitData).map(b => ({...b, fecha: b.fecha.replace('T', ' ').split('.')[0]})));
    } catch (e) {
      console.error("Error fetching bitacora", e);
    }
  }, []);

  // Carga inicial: solo lo que necesita el layout/los intervalos de alerta,
  // sin importar en qué ruta entró el usuario.
  const fetchData = useCallback(async () => {
    await Promise.allSettled([fetchRegistros(), fetchAlertas()]);
  }, [fetchRegistros, fetchAlertas]);

  const syncPendingRecords = useCallback(async ({ silent = false } = {}) => {
    // Un mismo barrido sincroniza registros Y lavados de filtros pendientes
    // — comparten la cola de localStorage (ver offlineQueue.js).
    const result = await syncOfflineQueue({ registroDiarioAPI, lavadoFiltroAPI });
    refreshPendingSync();

    if (result.synced > 0) {
      if (!silent) notify(`${result.synced} elemento(s) sincronizado(s)`, "success");
      await fetchRegistros();
    } else if (result.remaining > 0 && !silent) {
      notify("Hay elementos pendientes por sincronizar", "warning");
    }

    return result;
  }, [fetchRegistros, notify, refreshPendingSync]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (currentUser) syncPendingRecords();
    };
    const handleOffline = () => setIsOnline(false);
    const handleQueueChange = () => {
      const pendingRegistros = refreshPendingSync();
      setRegistros(prev => {
        const synced = prev.filter(r => !r.offlinePending);
        return [...synced, ...pendingRegistros];
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('aquareport:offline-queue-changed', handleQueueChange);
    if (currentUser) syncPendingRecords({ silent: true });

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('aquareport:offline-queue-changed', handleQueueChange);
    };
  }, [currentUser, refreshPendingSync, syncPendingRecords]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    profileAPI.getProfile().then(profile => {
      const user = mapUserToJSX(profile.data);
      localStorage.setItem(CACHED_USER_KEY, JSON.stringify(user));
      setCurrentUser(user);
      setLoading(false);
    }).catch((error) => {
      // Sin conexión (ej. la app de escritorio se abrió ya offline, con una
      // sesión de un login anterior todavía vigente): no se puede validar
      // el token contra el servidor, pero tampoco hay motivo para cerrar la
      // sesión — se entra con el último perfil que se guardó en caché y se
      // sigue como siempre (los datos se piden quedan en cola igual que
      // cualquier otra pérdida de conexión a mitad de turno). Un 401/403
      // real (token efectivamente inválido) sí cierra la sesión.
      if (isOfflineError(error)) {
        const cached = localStorage.getItem(CACHED_USER_KEY);
        if (cached) {
          try {
            setCurrentUser(JSON.parse(cached));
            setLoading(false);
            return;
          } catch {
            // Caché corrupta: cae al logout de abajo.
          }
        }
      }
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem(CACHED_USER_KEY);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchData();
    }
  }, [currentUser, fetchData]);

  useEffect(() => {
    if (!currentUser) return;
    const path = location.pathname;
    if (["/registros", "/usuarios", "/importar"].includes(path) && !usersLoadedRef.current) {
      usersLoadedRef.current = true;
      fetchUsuarios();
    }
    if (path === "/logs" && !logsLoadedRef.current) {
      logsLoadedRef.current = true;
      fetchLogs();
    }
    if (path === "/bitacora" && !bitacoraLoadedRef.current) {
      bitacoraLoadedRef.current = true;
      fetchBitacora();
    }
  }, [location.pathname, currentUser, fetchUsuarios, fetchLogs, fetchBitacora]);

  useEffect(() => {
    if (!currentUser) return;
    if (currentUser.rol === "operario") {
      alertIntervalRef.current = setInterval(async () => {
        const hora = getCurrentHour();
        setShowAlert(true);
        const nuevaAlerta = {
          tipo:"reminder",
          mensaje:`⏰ Recordatorio: Debes registrar los datos de la hora ${hora}`,
          urgente:false,
          leida: false
        };
        try {
          const res = await alertaAPI.create(nuevaAlerta);
          setAlertas(prev => [res.data, ...prev]);
        } catch (e) {
          setAlertas(prev => [{ id: Date.now(), ...nuevaAlerta, fecha: getNow() }, ...prev]);
        }
        notify(`⏰ Recuerda registrar la hora ${hora}`, "warning");
      }, 600000); // 10 minutes
    }

    checkMissingRef.current = setInterval(async () => {
      const hora = getCurrentHour();
      const hoy = getCurrentDate();
      const registrado = registrosRef.current.some(r => r.fecha === hoy && r.hora && r.hora.startsWith(hora));
      if (!registrado && currentUser.rol === "administrador") {
        const existe = alertasRef.current.some(a => a.mensaje.includes(hora) && !a.leida);
        if (!existe) {
          const nueva = {
            tipo:"missing_record",
            mensaje:`🚨 ALERTA: Sin registro en hora ${hora} del ${hoy}`,
            urgente:true,
            leida: false
          };
          try {
            const res = await alertaAPI.create(nueva);
            setAlertas(prev => [res.data, ...prev]);
          } catch (e) {
            setAlertas(prev => [{ id: Date.now(), ...nueva, fecha: getNow() }, ...prev]);
          }
        }
      }
    }, 60000);

    return () => {
      clearInterval(alertIntervalRef.current);
      clearInterval(checkMissingRef.current);
    };
    // registros/alertas deliberadamente fuera de las deps: se leen vía ref
    // (registrosRef/alertasRef) para que estos intervalos se creen una sola
    // vez por sesión en vez de reiniciarse (y perder su cuenta de 60s/10min)
    // cada vez que llega un registro o una alerta nueva.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, notify]);

  const handleLogin = async (username, password) => {
    try {
      const res = await authAPI.login({ username, password });
      localStorage.setItem('token', res.access);
      if (res.refresh) localStorage.setItem('refresh_token', res.refresh);
      const profile = await profileAPI.getProfile();
      const user = mapUserToJSX(profile.data);
      localStorage.setItem(CACHED_USER_KEY, JSON.stringify(user));
      setCurrentUser(user);
      addLog(user.nombre, "LOGIN", "Inicio de sesión exitoso");
      notify(`Bienvenido/a, ${user.nombre}`, "success");
      navigate("/");
    } catch (err) {
      notify("El usuario o contraseña invalidos", "error");
      throw err;
    }
  };

  const handleLogout = () => {
    addLog(currentUser.nombre, "LOGOUT", "Cierre de sesión");
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem(CACHED_USER_KEY);
    setCurrentUser(null);
    usersLoadedRef.current = false;
    logsLoadedRef.current = false;
    bitacoraLoadedRef.current = false;
    navigate("/login");
  };

  if (loading) return <div style={{background: theme.bg, minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", color: theme.text }}>Cargando...</div>;

  if (!currentUser) return (
    <Routes>
      <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
      <Route path="*" element={<Navigate to="/login" />} />
    </Routes>
  );

  return (
    <AquaLayout
      currentUser={currentUser}
      onLogout={handleLogout}
      unreadAlertas={alertas.filter(a => !a.leida).length}
      showAlert={showAlert}
      setShowAlert={setShowAlert}
      navigate={navigate}
    >
      <Suspense fallback={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', color: 'var(--text-tertiary)' }}>
          Cargando...
        </div>
      }>
      <Routes>
        <Route path="/" element={<DashboardPage registros={registros} alertas={alertas} currentUser={currentUser} />} />
        <Route path="/registros" element={<RegistrosPage registros={registros} fetchData={fetchRegistros} currentUser={currentUser} users={users} addLog={addLog} notify={notify} isOnline={isOnline} pendingSyncCount={pendingSyncCount} syncPendingRecords={syncPendingRecords} />} />
        <Route path="/ensayos" element={<EnsayosPage currentUser={currentUser} addLog={addLog} notify={notify} />} />
        <Route path="/inventario" element={<InventarioPage currentUser={currentUser} addLog={addLog} notify={notify} />} />
        <Route path="/lavado-filtros" element={<LavadoFiltrosPage registros={registros} currentUser={currentUser} addLog={addLog} notify={notify} isOnline={isOnline} syncPendingRecords={syncPendingRecords} />} />
        <Route path="/usuarios" element={currentUser.rol === "administrador_ti" ? <UsuariosPage users={users} fetchData={fetchUsuarios} currentUser={currentUser} addLog={addLog} notify={notify} /> : <Navigate to="/" />} />
        <Route path="/graficas" element={currentUser.rol !== "operario" ? <GraficasPage registros={registros} /> : <Navigate to="/" />} />
        <Route path="/alertas" element={<AlertasPage alertas={alertas} setAlertas={setAlertas} notify={notify} currentUser={currentUser} />} />
        <Route path="/logs" element={currentUser.rol === "administrador_ti" ? <LogsPage logs={logs} /> : <Navigate to="/" />} />
        <Route path="/bitacora" element={<BitacoraPage bitacora={bitacora} fetchData={fetchBitacora} currentUser={currentUser} addLog={addLog} notify={notify} />} />
        <Route path="/exportar" element={currentUser.rol !== "operario" ? <ExportarPage registros={registros} notify={notify} /> : <Navigate to="/" />} />
        <Route path="/gerencia" element={currentUser.rol !== "operario" ? <GerenciaPage registros={registros} currentUser={currentUser} notify={notify} /> : <Navigate to="/" />} />
        <Route path="/informes" element={currentUser.rol !== "operario" ? <InformesPage currentUser={currentUser} notify={notify} /> : <Navigate to="/" />} />
        <Route path="/prediccion" element={currentUser.rol !== "operario" ? <PrediccionPage /> : <Navigate to="/" />} />
        <Route path="/registros-mes" element={(currentUser.rol === "administrador" || currentUser.rol === "administrador_ti") ? <RegistrosMesPage notify={notify} /> : <Navigate to="/" />} />
        <Route path="/seguridad" element={currentUser.rol === "administrador_ti" ? <SeguridadPage currentUser={currentUser} addLog={addLog} notify={notify} /> : <Navigate to="/" />} />
        <Route path="/importar" element={currentUser.rol !== "operario" ? <ImportarPage fetchData={fetchRegistros} currentUser={currentUser} users={users} notify={notify} addLog={addLog} registros={registros} /> : <Navigate to="/" />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      </Suspense>
    </AquaLayout>
  );
}
